import { zodResolver } from '@hookform/resolvers/zod';
import { AppError } from '@repo/error';
import { errorMessage, statusLabel, t, validationError } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { PolicyCategorySchema } from '@repo/shared/schema';
import type { TSignal } from '@repo/shared/schema/report';
import { Button } from '@repo/ui/button';
import { Card, CardContent, CardHeader } from '@repo/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form';
import { Textarea } from '@repo/ui/textarea';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { trpc } from '@/providers/trpc';

const resolutionSchema = z.object({
  identityConfirmed: z.boolean(),
  rationale: z.string().trim().min(1, t('extra.explainYourReview')).max(4000),
});
export const SignalDetailCard = ({
  signal,
  reportId,
  onResolved,
}: {
  signal: TSignal;
  reportId: string;
  onResolved: () => void;
}) => {
  useLocale();

  const utils = trpc.useUtils();
  const { data: report } = trpc.report.getReport.useQuery({ reportId });
  const form = useForm<z.infer<typeof resolutionSchema>>({
    resolver: zodResolver(resolutionSchema, { error: validationError }),
    defaultValues: { rationale: '', identityConfirmed: false },
  });
  const mutation = trpc.report.updateSignalResolution.useMutation({
    onSuccess: () => {
      void utils.report.getReport.invalidate({ reportId });
      void utils.workspace.invalidate();
      onResolved();
    },
    onError: (cause) => {
      const error = AppError.parse(cause);
      toast.error(errorMessage(AppError.parse(error)));
    },
  });
  const submit = (resolution: 'CONFIRMED' | 'DISMISSED') =>
    form.handleSubmit(({ rationale, identityConfirmed }) => {
      if (report) {
        mutation.mutate({
          reportId,
          signalId: signal._id,
          data: { resolution, rationale, identityConfirmed, expectedRevision: report.revision },
        });
      }
    })();
  return (
    <Card>
      <CardHeader>
        <h4 className="font-medium">{signal.title}</h4>
      </CardHeader>
      <CardContent className="space-y-4">
        {signal.appliedRule && signal.severity && (
          <p className="rounded-md bg-muted p-3 text-sm">
            {t('policies.appliedRule')}: {policyRuleLabel(signal.appliedRule)} ·{' '}
            {t('policies.impact')}: {t(`policies.${signal.severity}`)}
          </p>
        )}
        <p className="text-sm">{signal.details}</p>
        <p className="text-sm">{signal.matchReason}</p>
        {signal.activity && (
          <div className="rounded border p-3 text-sm">
            <p>
              {signal.activity.platform} · {signal.activity.kind} ·{' '}
              {signal.activity.author || signal.activity.account}
            </p>
            {signal.activity.publishedAt && <p>{signal.activity.publishedAt}</p>}
            {/^https?:\/\//.test(signal.activity.permalink) && (
              <a
                className="underline"
                href={signal.activity.permalink}
                rel="noopener noreferrer"
                target="_blank"
              >
                {signal.activity.permalink}
              </a>
            )}
          </div>
        )}
        <p className="text-muted-foreground text-sm">
          {t('auth.identityMatch')} {statusLabel(signal.match)} {t('auth.review')}{' '}
          {statusLabel(signal.resolution ?? 'PENDING')}
        </p>
        {signal.sourceUrl && signal.citations.length === 0 && (
          <a
            className="text-sm underline"
            href={signal.sourceUrl}
            rel="noopener noreferrer"
            target="_blank"
          >
            {signal.sourceUrl}
          </a>
        )}
        {[
          ...new Map(signal.citations.map((citation) => [citation.evidenceId, citation])).values(),
        ].map((citation) => (
          <EvidenceSource
            key={`${citation.evidenceId}-${citation.excerpt}`}
            reportId={reportId}
            {...citation}
          />
        ))}
        <p className="text-xs">
          {signal.classification ?? t('reports.unassessed')} ·{' '}
          {signal.severity ?? t('extra.legacySeverity')} · {signal.eventContext}
        </p>
        {report?.reviewHistory
          ?.filter((event) => String(event.signalId) === signal._id)
          .map((event, index) => (
            <p className="text-xs" key={index}>
              {String(event.to ?? '')} · {String(event.rationale ?? '')} · {String(event.at ?? '')}
            </p>
          ))}
        {signal.rationale && (
          <p className="text-sm">
            {t('auth.officerRationale')} {signal.rationale}
          </p>
        )}
        {report?.actions.canReview && (
          <Form {...form}>
            <form onSubmit={(event) => event.preventDefault()}>
              <fieldset className="space-y-3" disabled={mutation.isPending}>
                <FormField
                  control={form.control}
                  name="rationale"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('auth.reviewRationale')}</FormLabel>
                      <FormControl>
                        <Textarea {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {signal.match === 'uncertain' && (
                  <FormField
                    control={form.control}
                    name="identityConfirmed"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex gap-2">
                          <input
                            checked={field.value}
                            onChange={(event) => field.onChange(event.target.checked)}
                            type="checkbox"
                          />
                          {t('auth.iVerifiedThatThisEvidenceConcernsThisSubject')}{' '}
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                )}
                <div className="flex gap-2">
                  <Button onClick={() => void submit('CONFIRMED')} type="button">
                    {t('auth.confirmFinding')}{' '}
                  </Button>
                  <Button onClick={() => void submit('DISMISSED')} type="button" variant="outline">
                    {t('auth.dismissFinding')}{' '}
                  </Button>
                </div>
              </fieldset>
            </form>
          </Form>
        )}
      </CardContent>
    </Card>
  );
};

const EvidenceSource = ({
  reportId,
  evidenceId,
  excerpt,
}: {
  reportId: string;
  evidenceId: string;
  excerpt: string;
}) => {
  useLocale();

  const { data } = trpc.report.getReportEvidence.useQuery(
    { reportId, evidenceId },
    {
      staleTime: 60_000,
      enabled: !evidenceId.startsWith('document:') && !evidenceId.startsWith('submitted:'),
    },
  );
  return (
    <div className="space-y-2 rounded border p-3">
      <blockquote className="border-s-2 ps-3 text-sm">{excerpt}</blockquote>
      {data?.url && /^https?:\/\//.test(data.url) ? (
        <a
          className="block break-all text-sm underline"
          href={data.url}
          rel="noopener noreferrer"
          target="_blank"
        >
          {data.title || data.url}
        </a>
      ) : data?.title ? (
        <p className="text-muted-foreground text-xs">{data.title}</p>
      ) : null}
    </div>
  );
};

const policyRuleLabel = (rule: string) => {
  if (rule === 'informational' || rule === 'discrepancy' || rule === 'seriousFinancialCrime') {
    return t(`policies.${rule}`);
  }
  const category = PolicyCategorySchema.safeParse(rule.replace('category.', ''));
  return category.success ? t(`policies.${category.data}`) : rule;
};
